import { ChevronRight } from "lucide-react";
import { Link, useParams } from "@tanstack/react-router";
import { useSidebar } from "./SidebarContext";
import { SidebarTooltip } from "./Tooltip";
import { useTranslation } from "@/packages/utils";
import type { RecentAudit } from "./types";

interface SidebarRecentAuditsProps {
  audits: RecentAudit[];
}

export function SidebarRecentAudits({ audits }: SidebarRecentAuditsProps) {
  const { collapsed } = useSidebar();
  const { t } = useTranslation(["sidebar"]);
  const { workspaceSlug } = useParams({ strict: false });

  return (
    <div className="space-y-px">
      {audits.map((audit) => {
        const Icon = audit.icon;

        const content = (
          <>
            <Icon
              size={16}
              strokeWidth={1.75}
              className="shrink-0 transition-colors duration-150"
              style={audit.iconColor ? { color: audit.iconColor } : undefined}
            />
            {!collapsed && (
              <span className="flex-1 truncate text-left text-[13px] tracking-[-0.01em]">
                {audit.name}
              </span>
            )}
          </>
        );

        const className = `group/item flex items-center rounded-md text-secondary hover:bg-card-hover/40 hover:text-primary transition-all duration-150 ${
          collapsed
            ? "justify-center px-0 mx-auto w-10 h-8"
            : "gap-2.5 py-[5px] pl-3.5 pr-2"
        }`;

        return (
          <SidebarTooltip key={audit.id} label={audit.name} enabled={collapsed}>
            <Link
              to={audit.to}
              params={(prev) => prev}
              search={(prev) => prev}
              className={className}
            >
              {content}
            </Link>
          </SidebarTooltip>
        );
      })}

      {!collapsed && (
        <Link
          to="/$workspaceSlug/audits"
          params={{ workspaceSlug: workspaceSlug ?? "" }}
          className="group/show flex items-center gap-2 rounded-md py-[5px] pl-3.5 pr-2 text-[12px] text-muted hover:text-secondary transition-colors duration-150"
        >
          <span>{t("sidebar:actions.showAll")}</span>
          <ChevronRight
            size={12}
            strokeWidth={2}
            className="opacity-0 -translate-x-1 group-hover/show:opacity-100 group-hover/show:translate-x-0 transition-all duration-150"
          />
        </Link>
      )}
    </div>
  );
}
